import { useEffect } from 'react';
import {
  Modal,
  Form as AntForm,
  Input,
  InputNumber,
  Select,
  App,
} from 'antd';
import { useForm, FormProvider } from 'react-hook-form';
import type { SubmitHandler, SubmitErrorHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { authFetch } from '../../utils/api';
import {
  FormField,
  FormSection,
  FormActions,
  TargetBrandField,
} from '../../shared/form';
import { targetBrandForUpsert } from '../../shared/form/writeTargetBrand';
import {
  PromoCodeUpsert,
  type PromoCodeUpsertValues,
} from '../../schemas/promoCode';
import {
  ValidityMinutesField,
  PerDeviceRedeemLimitField,
  AllowedNodeTiersField,
  useNodeTierOptions,
} from './promoFields';
import type { PromoCodeRecord } from './index';

interface Props {
  open: boolean;
  /** null = 新增、PromoCodeRecord = 編輯 */
  record: PromoCodeRecord | null;
  /**
   * 去品牌化 M4：目前檢視品牌，僅用於「新增」時預填表單的「目標品牌」欄位
   * （檢視=單品牌 → 顯式帶入可再改；檢視=all → null 留空強制手選，對齊 users 頁）。
   * 實際寫入品牌一律取表單顯式選擇的 targetBrand 欄位，絕不繼承檢視範圍 / activeBrand。
   */
  viewBrand: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

const DEFAULTS: PromoCodeUpsertValues = {
  targetBrand: '',
  code: '',
  name: '',
  advertiser: null,
  campaign: null,
  grantMb: 1024,
  validitySeconds: 86400,
  perDeviceCapMb: 3072,
  perDeviceRedeemLimit: 0,
  allowedNodeTiers: null,
  totalRedemptionLimit: 0,
  startAt: null,
  endAt: null,
  status: 'active',
};

/** zod 驗證失敗時，把欄位 key 轉成 UI 上看得懂的中文名（供 onInvalid toast）。 */
const FIELD_LABELS: Record<string, string> = {
  targetBrand: '目標品牌',
  code: '兌換碼',
  name: '名稱',
  advertiser: '廣告商/通路',
  campaign: '活動代號',
  grantMb: '發放流量 (MB)',
  validitySeconds: '有效時長 (分鐘)',
  perDeviceCapMb: '單裝置累計上限 (MB)',
  perDeviceRedeemLimit: '單裝置兌換次數上限',
  allowedNodeTiers: '可用節點等級 (ACL)',
  totalRedemptionLimit: '總兌換上限',
  status: '狀態',
  startAt: '開始時間',
  endAt: '結束時間',
};

/**
 * 兌換碼新增/編輯表單 — 鏡像 TierFormModal 的 RHF + zod + shared/form 寫法。
 * BO 強制：不可用 AntD `Form.useForm`+`rules`，改用 `react-hook-form` +
 * `zodResolver` + `<FormProvider>` + `<FormField control={<Input/>}>`。
 *
 * Ship 1：startAt/endAt（日期）暫不在 UI 開放，預設不送＝null（立即生效/永久）。
 */
export const PromoCodeFormModal: React.FC<Props> = ({
  open,
  record,
  viewBrand,
  onClose,
  onSuccess,
}) => {
  const isEdit = !!record;
  // React 19 下 antd 靜態 `message.*` 走 legacy ReactDOM.render 不渲染（專案未裝
  // @ant-design/v5-patch-for-react-19）→ 改用 App context 綁定的 message，確保
  // 驗證警告 / 儲存錯誤 toast 真的顯示（App.tsx 已用 <AntdApp> 包裹整棵樹）。
  const { message } = App.useApp();
  // VPN-81 B5 — 節點等級 ACL 的建議選項（distinct node.minTier），開表單時抓一次。
  const tierOptions = useNodeTierOptions(open);
  const form = useForm<PromoCodeUpsertValues>({
    resolver: zodResolver(PromoCodeUpsert),
    mode: 'onBlur',
    defaultValues: DEFAULTS,
  });

  // 開啟時注入既有值或重設
  useEffect(() => {
    if (open) {
      if (record) {
        form.reset({
          // 編輯 = 既有 row 寫入，目標品牌固定 = row.brand（不顯示欄位、不可改）。
          targetBrand: record.brand,
          code: record.code,
          name: record.name ?? '',
          advertiser: (record as { advertiser?: string | null }).advertiser ?? null,
          campaign: (record as { campaign?: string | null }).campaign ?? null,
          grantMb: record.grantMb ?? 1024,
          validitySeconds: record.validitySeconds ?? 86400,
          perDeviceCapMb: record.perDeviceCapMb ?? 3072,
          perDeviceRedeemLimit: record.perDeviceRedeemLimit ?? 0,
          allowedNodeTiers: Array.isArray(record.allowedNodeTiers)
            ? record.allowedNodeTiers
            : null,
          totalRedemptionLimit: record.totalRedemptionLimit ?? 0,
          startAt: record.startAt ?? null,
          endAt: record.endAt ?? null,
          status: record.status ?? 'active',
        });
      } else {
        // 新增：檢視=單品牌 → 預填該品牌（顯式可改）；檢視=all → 留空強制手選。
        form.reset({ ...DEFAULTS, targetBrand: viewBrand ?? '' });
      }
    }
  }, [open, record, form, viewBrand]);

  const onSubmit: SubmitHandler<PromoCodeUpsertValues> = async (values) => {
    // M4：寫入一律顯式帶目標品牌，絕不繼承檢視範圍 / activeBrand。
    // - 新增：目標品牌 = 表單「目標品牌」欄位顯式選擇（TargetBrandField，聚合檢視
    //   強制手選；zod 已擋空值，此處 fail-closed 再擋一次）。
    // - 編輯（既有 row）：目標品牌 = 該兌換碼 row 的實際品牌（record.brand，清單已帶
    //   brand 欄），對齊 backend resolveExistingRowWriteTargetBrand（顯式與 row 不符
    //   → 403、缺顯式 enforce ON → 400）。
    const t = targetBrandForUpsert(isEdit, record?.brand, values.targetBrand);
    if (!t.ok) {
      message.error(t.reason);
      return;
    }
    const payload: Record<string, unknown> = {
      ...values,
      targetBrand: t.brand,
    };
    if (isEdit) delete payload.code; // code 不可改
    const url = isEdit
      ? `/api/admin/promo-codes/${record!.id}`
      : '/api/admin/promo-codes';
    try {
      const res = await authFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'X-Target-Brand': t.brand },
        body: JSON.stringify(payload),
      });
      // 後端非 2xx（如 brand-gate 403「兌換碼僅限 wudi_star 品牌管理」）仍回
      // JSON body；但 CDN/proxy 502 可能回非 JSON → res.json() 會 throw，用
      // .catch 收斂成 null 才不會整個 submit 靜默失敗。
      const result = await res.json().catch(() => null);
      if (result && result.code === 0) {
        message.success(isEdit ? '已更新' : '已建立');
        onSuccess();
      } else if (result) {
        message.error(result.msg || `儲存失敗（HTTP ${res.status}）`);
        if (result.msg?.includes('已存在')) {
          form.setError('code', { message: result.msg });
        }
      } else {
        message.error(`儲存失敗：伺服器回應異常（HTTP ${res.status}）`);
      }
    } catch (e) {
      message.error(
        `儲存失敗：${e instanceof Error ? e.message : '網路或伺服器錯誤'}`
      );
    }
  };

  /**
   * zod 驗證失敗時的可見回饋（先前缺這個 callback → handleSubmit 靜默吞掉，
   * 點「建立兌換碼」完全無反應）。對齊節點編輯頁 commit 6289b6c 的 pattern：
   * 聚焦第一個錯誤欄位 + message.warning 列出哪些欄位待修正。
   */
  const onInvalid: SubmitErrorHandler<PromoCodeUpsertValues> = (errors) => {
    const keys = Object.keys(errors);
    if (keys.length === 0) return;
    const names = keys.map((k) => FIELD_LABELS[k] ?? k).join('、');
    message.warning(`有 ${keys.length} 個欄位待修正：${names}`);
    try {
      form.setFocus(keys[0] as keyof PromoCodeUpsertValues);
    } catch {
      /* setFocus best-effort：Controller ref 可能未註冊，欄位下方仍有紅字提示 */
    }
  };

  return (
    <Modal
      title={isEdit ? '編輯兌換碼' : '新增兌換碼'}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnHidden
      width={600}
    >
      <FormProvider {...form}>
        <AntForm layout="vertical" onFinish={form.handleSubmit(onSubmit, onInvalid)}>
          <FormSection title="基本資訊" description="兌換碼與辨識資訊">
            {!isEdit ? (
              // M4：新增時顯式選目標品牌（聚合檢視強制手選）；編輯固定 row.brand 不顯示。
              <TargetBrandField<PromoCodeUpsertValues> name="targetBrand" />
            ) : null}
            <FormField<PromoCodeUpsertValues>
              name="code"
              label="兌換碼"
              required
              help={isEdit ? '兌換碼建立後不可修改' : undefined}
              control={
                <Input
                  placeholder="例如：WD-SUMMER2026"
                  disabled={isEdit}
                />
              }
            />
            <FormField<PromoCodeUpsertValues>
              name="name"
              label="名稱"
              control={<Input placeholder="後台辨識用，例如：五一活動" />}
            />
            <FormField<PromoCodeUpsertValues>
              name="advertiser"
              label="廣告商/通路"
              control={<Input placeholder="選填" />}
            />
            <FormField<PromoCodeUpsertValues>
              name="campaign"
              label="活動代號"
              control={<Input placeholder="選填" />}
            />
          </FormSection>

          <FormSection title="發放設定" description="兌換後給予的免費流量與有效期">
            <FormField<PromoCodeUpsertValues>
              name="grantMb"
              label="發放流量 (MB)"
              required
              control={<InputNumber min={0} style={{ width: '100%' }} />}
            />
            <ValidityMinutesField />
            <FormField<PromoCodeUpsertValues>
              name="perDeviceCapMb"
              label="單裝置累計上限 (MB)"
              help="0 代表不限制"
              control={<InputNumber min={0} style={{ width: '100%' }} />}
            />
            <PerDeviceRedeemLimitField />
            <FormField<PromoCodeUpsertValues>
              name="totalRedemptionLimit"
              label="總兌換上限"
              help="0 代表不限制"
              control={
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  placeholder="0 = 不限"
                />
              }
            />
          </FormSection>

          <FormSection
            title="使用範圍"
            description="限制此兌換碼發放的權益可用於哪些節點等級"
          >
            <AllowedNodeTiersField tierOptions={tierOptions} />
          </FormSection>

          <FormSection title="狀態">
            <FormField<PromoCodeUpsertValues>
              name="status"
              label="狀態"
              control={
                <Select
                  options={[
                    { value: 'active', label: '啟用' },
                    { value: 'disabled', label: '停用' },
                  ]}
                />
              }
            />
          </FormSection>

          <FormActions
            onCancel={onClose}
            submitting={form.formState.isSubmitting}
            dirty={form.formState.isDirty}
            submitLabel={isEdit ? '儲存變更' : '建立兌換碼'}
          />
        </AntForm>
      </FormProvider>
    </Modal>
  );
};

export default PromoCodeFormModal;
